import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs } from 'react-router';
import { System } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { listOpenIssuesForSystemQuery } from '~/shared/api/issues.queries';
import { getSystemQuery } from '~/shared/api/system.queries';
import { IssuesLoaderData } from '~/shared/hooks/issues-hooks';
import { getWaterStoredKpiQuery, listKpis } from './api/kpi.queries';
import { checkAuthorization } from '~/shared/auth/auth-utils';
import { assertIsDefined } from '~/types/assert-type';
import { KpiListModel } from '~/pages/Dashboard/models/KpiListModel';
import { WaterStoredKpiModel } from '~/pages/Dashboard/models/WaterStoredKpiModel';
import { SystemModel } from '~/shared/models/system/SystemModel';

export type DashboardPageLoaderResult = IssuesLoaderData & {
  system: System;
  kpis: KpiListModel;
  waterStoredKpi: WaterStoredKpiModel;
};

export const dashboardLoader =
  (queryClient: QueryClient) =>
  async ({
    params,
    request,
  }: LoaderFunctionArgs): Promise<Response | DashboardPageLoaderResult> => {
    await checkAuthorization(queryClient, request);

    const { systemId } = params;

    assertIsDefined(systemId);

    const system = await queryClient.ensureQueryData(getSystemQuery(systemId));

    const systemModel = new SystemModel({ system });

    const [listIssuesResponse, kpisModel, waterStoredKpiModel] =
      await Promise.all([
        queryClient.ensureQueryData(
          listOpenIssuesForSystemQuery({ systemName: system.name }),
        ),
        queryClient.ensureQueryData(
          listKpis({
            systemName: system.name,
            kpiMetricTypes: systemModel.availableKpis,
          }),
        ),
        queryClient.ensureQueryData(
          getWaterStoredKpiQuery({
            systemName: system.name,
          }),
        ),
      ]);

    return {
      system,
      issues: listIssuesResponse,
      kpis: kpisModel,
      waterStoredKpi: waterStoredKpiModel,
    };
  };
